import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { createApprentice, getMeta, listApprentices, updateApprentice } from "./api";
import { weekdayLabels } from "./labels";
import { formatDay } from "../safety/format";
import "../safety/safety.css";

const DATE_FIELDS = ["startDate", "endDate", "chamberRegisteredOn", "midExamDate", "finalExamDate"];

export const apprenticeStatusLabels = t => ({
  active: t("In training"),
  finished: t("Finished"),
  terminated: t("Terminated")
});

/** Create or edit an apprentice record. */
export const ApprenticeDialog = ({ open, record, meta, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const weekdays = weekdayLabels(t);
  const statuses = apprenticeStatusLabels(t);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    const values = record?._id
      ? { ...record, trainer: record.trainer || "" }
      : { schoolDays: [], cadence: "weekly", trainer: "", status: "active" };
    DATE_FIELDS.forEach(key => {
      values[key] = values[key] ? dayjs(values[key]) : null;
    });
    form.setFieldsValue(values);
  }, [open]);

  const submit = async values => {
    const body = { ...values, trainer: values.trainer || null };
    DATE_FIELDS.forEach(key => {
      body[key] = values[key] ? values[key].format("YYYY-MM-DD") : "";
    });
    setBusy(true);
    try {
      if (record?._id) {
        delete body.employee;
        await updateApprentice(record._id, body);
      } else {
        await createApprentice(body);
      }
      Success(t("Saved"));
      onSaved();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const employees = meta?.employees || [];

  return (
    <Modal
      rootClassName="apprenticeship-page"
      open={open}
      width={720}
      title={record?._id ? t("Edit apprentice") : t("New apprentice")}
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="employee" label={t("Employee")} rules={[{ required: true }]}>
              <Select
                showSearch
                disabled={Boolean(record?._id)}
                optionFilterProp="label"
                options={employees.map(e => ({ value: e._id, label: e.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="trade"
              label={t("Trade (Ausbildungsberuf)")}
              rules={[{ required: true }]}
            >
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="startDate" label={t("Start of training")} rules={[{ required: true }]}>
              <DatePicker className="sf-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="endDate" label={t("End of training")}>
              <DatePicker className="sf-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="trainer" label={t("Trainer")}>
              <Select
                showSearch
                optionFilterProp="label"
                options={[
                  { value: "", label: t("Company owner") },
                  ...employees
                    .filter(e => e._id !== record?.employee)
                    .map(e => ({ value: e._id, label: e.name }))
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="school" label={t("Vocational school")}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="schoolDays" label={t("School days")}>
              <Checkbox.Group
                options={[1, 2, 3, 4, 5, 6].map(n => ({
                  value: n,
                  label: weekdays[n - 1].slice(0, 2)
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="chamber" label={t("Chamber (HWK)")}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="chamberRegistrationNumber" label={t("Registration number")}>
              <Input maxLength={60} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="chamberRegisteredOn" label={t("Registered on")}>
              <DatePicker className="sf-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="midExamDate" label={t("Intermediate exam")}>
              <DatePicker className="sf-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="finalExamDate" label={t("Final exam")}>
              <DatePicker className="sf-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="cadence" label={t("Record form")}>
              <Select
                options={[
                  { value: "weekly", label: t("Weekly") },
                  { value: "daily", label: t("Daily") }
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="status" label={t("Status")}>
              <Select
                options={Object.entries(statuses).map(([value, label]) => ({ value, label }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="note" label={t("Note")}>
          <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} maxLength={2000} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/** Apprentices of the company with their Berichtsheft status (managers). */
const Apprenticeship = () => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const statuses = apprenticeStatusLabels(t);

  const load = async () => {
    setLoading(true);
    try {
      const [metaRes, res] = await Promise.all([getMeta(), listApprentices()]);
      setMeta(metaRes.data);
      setData(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const open = id => history.push(`/user/berichtsheft/apprentices/${id}`);

  return (
    <div className="apprenticeship-page">
      <PageToolbar
        actions={
          meta?.can.create && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({})}>
              {t("New apprentice")}
            </Button>
          )
        }
      />
      {!loading && !data.length ? (
        <Card>
          <Empty
            description={
              <>
                <Typography.Title level={5}>{t("Digital training record")}</Typography.Title>
                <Typography.Paragraph type="secondary" className="sf-empty-text">
                  {t(
                    "Apprentices write their weekly record on the phone, prefilled from their working times. The trainer reviews and signs it, and the signed weeks are exported as PDF for the chamber."
                  )}
                </Typography.Paragraph>
              </>
            }
          >
            {meta?.can.create && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({})}>
                {t("New apprentice")}
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        <ResponsiveTable
          rowKey="_id"
          loading={loading}
          dataSource={data}
          onRow={record => ({ onDoubleClick: () => open(record._id) })}
          pagination={false}
          columns={[
            {
              title: t("Apprentice"),
              key: "name",
              mobile: "title",
              render: (_, item) => (
                <Link to={`/user/berichtsheft/apprentices/${item._id}`}>
                  <Typography.Text strong>{item.name}</Typography.Text>
                </Link>
              )
            },
            {
              title: t("Trade"),
              key: "trade",
              mobile: "subtitle",
              render: (_, item) => (
                <span className="sf-wrap">
                  {item.trade}
                  {item.trainingYearNow ? ` · ${t("Year {{n}}", { n: item.trainingYearNow })}` : ""}
                </span>
              )
            },
            { title: t("Trainer"), dataIndex: "trainerName", key: "trainer" },
            {
              title: t("Status"),
              key: "status",
              mobile: "badge",
              render: (_, item) => (
                <Space size={[4, 4]} wrap>
                  {item.status !== "active" && <Tag>{statuses[item.status]}</Tag>}
                  {item.missing > 0 && (
                    <Tag color="error">{t("{{n}} weeks missing", { n: item.missing })}</Tag>
                  )}
                  {item.toReview > 0 && (
                    <Tag color="processing">{t("{{n}} to review", { n: item.toReview })}</Tag>
                  )}
                  {item.status === "active" && !item.missing && !item.toReview && (
                    <Tag color="success">{t("Up to date")}</Tag>
                  )}
                </Space>
              )
            },
            {
              title: t("Period"),
              key: "period",
              render: (_, item) => `${formatDay(item.startDate)} – ${formatDay(item.endDate)}`
            },
            {
              title: "",
              key: "actions",
              mobile: "actions",
              render: (_, item) =>
                meta?.can.update && (
                  <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(item)}>
                    {t("Edit")}
                  </Button>
                )
            }
          ]}
        />
      )}
      <ApprenticeDialog
        open={Boolean(editing)}
        record={editing}
        meta={meta}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    </div>
  );
};

export default Apprenticeship;
